/**
 * VendorsPage — manage buyers / retailers and QR scan display settings.
 * Responsive, accessible, uses the Grafty Tailwind design system.
 */
import { useState, useEffect, useRef } from 'react'
import { apiError } from '../lib/errors'
import { Store, Plus, Pencil, Trash2, Upload, X, Check, ExternalLink } from 'lucide-react'
import { api } from '../lib/api'

interface Vendor {
  id: string
  name: string
  slug: string
  logo_url?: string
  website_url?: string
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  address?: string
  notes?: string
  is_active: boolean
  created_at: string
}

interface QRDisplaySettings {
  qr_vendor_branding: boolean
  qr_show_farm_identity: boolean
  qr_farm_attribution_label: string
}

// ── Toggle switch ──────────────────────────────────────────────────────────

function Toggle({
  checked,
  onChange,
  label,
  hint,
  disabled = false,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  hint?: string
  disabled?: boolean
}) {
  return (
    <label className={`flex items-start gap-4 py-3 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 mt-0.5
          ${checked ? 'bg-mango-400' : 'bg-ink-ghost/30'}`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-soft transition-transform duration-200
            ${checked ? 'translate-x-[22px]' : 'translate-x-0.5'}`}
        />
      </button>
      <div className="flex-1">
        <div className="text-sm font-medium text-ink">{label}</div>
        {hint && <div className="text-xs text-ink-soft mt-0.5 leading-relaxed">{hint}</div>}
      </div>
    </label>
  )
}

// ── Vendor form modal ──────────────────────────────────────────────────────

function VendorModal({
  vendor,
  onClose,
  onSaved,
}: {
  vendor: Vendor | null
  onClose: () => void
  onSaved: (v: Vendor) => void
}) {
  const isEdit = !!vendor
  const [form, setForm] = useState({
    name: vendor?.name ?? '',
    website_url: vendor?.website_url ?? '',
    contact_name: vendor?.contact_name ?? '',
    contact_email: vendor?.contact_email ?? '',
    contact_phone: vendor?.contact_phone ?? '',
    address: vendor?.address ?? '',
    notes: vendor?.notes ?? '',
    logo_url: vendor?.logo_url ?? '',
  })
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(vendor?.logo_url ?? null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (f.size > 2 * 1024 * 1024) {
      setError('Logo must be under 2 MB')
      return
    }
    setLogoFile(f)
    setLogoPreview(URL.createObjectURL(f))
  }

  const set = <K extends keyof typeof form>(key: K, value: typeof form[K]) =>
    setForm(f => ({ ...f, [key]: value }))

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Vendor name is required'); return }
    setSaving(true); setError(null)

    try {
      const body = {
        name: form.name,
        website_url: form.website_url || undefined,
        contact_name: form.contact_name || undefined,
        contact_email: form.contact_email || undefined,
        contact_phone: form.contact_phone || undefined,
        address: form.address || undefined,
        notes: form.notes || undefined,
        logo_url: form.logo_url || undefined,
      }
      const resp = isEdit
        ? await api.put<Vendor>(`/vendors/${vendor!.id}`, body)
        : await api.post<Vendor>('/vendors', body)
      const saved: Vendor = resp.data

      if (logoFile && saved.id) {
        try {
          const init = await api.post<{ upload_path: string }>(`/vendors/${saved.id}/logo/initiate`)
          const { upload_path } = init.data
          const publicURL = `https://storage.googleapis.com/grafty-prod-assets/${upload_path}`
          await api.post(`/vendors/${saved.id}/logo/confirm`, { public_url: publicURL })
          saved.logo_url = publicURL
        } catch { /* logo upload non-fatal */ }
      }
      onSaved(saved)
    } catch (e) {
      setError(apiError(e, 'Failed to save vendor'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="vendor-modal-title"
      onClick={e => e.target === e.currentTarget && onClose()}
      className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto animate-fade-in"
    >
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-strong my-8 animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-ghost/15">
          <h2 id="vendor-modal-title" className="font-display text-lg font-bold text-ink">
            {isEdit ? 'Edit vendor' : 'Add vendor'}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-ink-muted hover:text-ink hover:bg-canvas-subtle p-1.5 rounded-md transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="label">Vendor logo</label>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-20 h-20 rounded-xl bg-canvas-subtle border-2 border-dashed border-ink-ghost/30 flex items-center justify-center overflow-hidden hover:border-mango-400 hover:bg-mango-50/50 transition-colors flex-shrink-0"
              >
                {logoPreview ? (
                  <img src={logoPreview} alt="" className="w-full h-full object-contain p-1" />
                ) : (
                  <Store className="text-ink-muted" size={28} strokeWidth={1.5} />
                )}
              </button>
              <div>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="btn-secondary text-xs py-1.5 px-3"
                >
                  <Upload size={13} />
                  {logoPreview ? 'Change' : 'Upload'}
                </button>
                <div className="text-xs text-ink-muted mt-1.5">
                  PNG or SVG, max 2 MB. Shown on QR scan pages.
                </div>
              </div>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
            </div>
          </div>

          <div>
            <label className="label">Or logo URL</label>
            <input type="url" value={form.logo_url} onChange={e => set('logo_url', e.target.value)}
              placeholder="https://vendor.com/logo.png" className="input" />
          </div>

          <div>
            <label className="label">Vendor name <span className="text-danger">*</span></label>
            <input type="text" value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="Whole Foods Market" className="input" required />
          </div>

          <div>
            <label className="label">Website</label>
            <input type="url" value={form.website_url} onChange={e => set('website_url', e.target.value)}
              placeholder="https://vendor.com" className="input" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Contact name</label>
              <input type="text" value={form.contact_name} onChange={e => set('contact_name', e.target.value)}
                placeholder="Jane Smith" className="input" />
            </div>
            <div>
              <label className="label">Contact email</label>
              <input type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)}
                placeholder="jane@vendor.com" className="input" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Phone</label>
              <input type="tel" value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)}
                placeholder="(555) 123-4567" className="input" />
            </div>
            <div>
              <label className="label">Address</label>
              <input type="text" value={form.address} onChange={e => set('address', e.target.value)}
                placeholder="123 Market St, Miami FL" className="input" />
            </div>
          </div>

          <div>
            <label className="label">Internal notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="Not shown on QR pages" rows={2} className="input resize-none" />
          </div>

          {error && (
            <div className="rounded-lg bg-danger/10 border border-danger/25 text-danger text-sm px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="flex gap-2 px-6 py-4 border-t border-ink-ghost/15">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary flex-[2]">
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add vendor'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Vendor card ────────────────────────────────────────────────────────────

function VendorCard({ vendor, onEdit, onDelete }: { vendor: Vendor; onEdit: () => void; onDelete: () => void }) {
  return (
    <div className="card p-4 sm:p-5 flex items-center gap-4 hover:shadow-medium transition-shadow group">
      <div className="w-14 h-14 rounded-xl bg-canvas-subtle border border-ink-ghost/15 flex items-center justify-center overflow-hidden flex-shrink-0">
        {vendor.logo_url
          ? <img src={vendor.logo_url} alt="" className="w-full h-full object-contain p-1" />
          : <Store className="text-ink-muted" size={24} strokeWidth={1.5} />
        }
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-semibold text-ink truncate">{vendor.name}</div>
        {vendor.contact_email && (
          <div className="text-xs text-ink-muted truncate mt-0.5">{vendor.contact_email}</div>
        )}
        {vendor.website_url && (
          <a href={vendor.website_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-mango-500 hover:text-mango-700 mt-1 truncate"
            onClick={e => e.stopPropagation()}>
            {vendor.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            <ExternalLink size={10} />
          </a>
        )}
      </div>

      <div className="flex gap-1.5 flex-shrink-0 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={onEdit} aria-label={`Edit ${vendor.name}`} className="btn-ghost p-2">
          <Pencil size={15} />
        </button>
        <button onClick={onDelete} aria-label={`Remove ${vendor.name}`}
          className="btn-ghost p-2 hover:text-danger hover:bg-danger/10">
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  )
}

// ── QR Display Settings panel ──────────────────────────────────────────────

function QRSettingsPanel({ settings, onChange }: { settings: QRDisplaySettings; onChange: (s: QRDisplaySettings) => void }) {
  const toggle = <K extends keyof QRDisplaySettings>(key: K, val: QRDisplaySettings[K]) =>
    onChange({ ...settings, [key]: val })

  return (
    <div className="card-raised">
      <div className="px-5 py-4 border-b border-ink-ghost/10">
        <h3 className="font-display text-base font-bold text-ink">QR Scan Page Display</h3>
        <p className="text-xs text-ink-soft mt-0.5">What consumers see when they scan a fruit QR code</p>
      </div>

      <div className="px-5 py-2 divide-y divide-ink-ghost/10">
        <Toggle checked={settings.qr_vendor_branding} onChange={v => toggle('qr_vendor_branding', v)}
          label="Show vendor branding"
          hint="When a harvest is sold to a vendor, their logo appears prominently on the QR scan page." />
        <Toggle checked={settings.qr_show_farm_identity} onChange={v => toggle('qr_show_farm_identity', v)}
          label="Show farm identity"
          hint="Show your farm name on QR scan pages. Enable to promote farm recognition. Disable for fully white-label vendor pages." />
      </div>

      {settings.qr_show_farm_identity && (
        <div className="px-5 pb-5 pt-3 border-t border-ink-ghost/10 animate-slide-up">
          <label className="label">Farm attribution label</label>
          <select value={settings.qr_farm_attribution_label}
            onChange={e => onChange({ ...settings, qr_farm_attribution_label: e.target.value })}
            className="input cursor-pointer">
            {['Grown by', 'Sourced by', 'Produced by', 'Harvested by', 'Farmed by', 'From'].map(l => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
          <p className="text-xs text-ink-muted mt-2">
            Appears before your farm name: <span className="font-medium text-ink-soft">"{settings.qr_farm_attribution_label} Sunshine Mango Farm"</span>
          </p>
        </div>
      )}

      <div className="mx-5 mb-5 mt-3 p-4 rounded-xl bg-gradient-to-br from-mango-50 to-grove-50 border border-mango-100">
        <div className="text-xs font-semibold uppercase tracking-wider text-mango-700 mb-2">Preview</div>
        {settings.qr_vendor_branding && (
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-9 h-9 rounded-lg bg-white border border-ink-ghost/15 flex items-center justify-center">
              <Store size={16} className="text-ink-muted" />
            </div>
            <div>
              <div className="text-sm font-semibold text-ink">Vendor Name</div>
              <div className="text-xs text-ink-muted">Retailer</div>
            </div>
          </div>
        )}
        {settings.qr_show_farm_identity && (
          <div className="text-xs text-ink-soft">
            {settings.qr_farm_attribution_label} <span className="text-ink font-medium">Your Farm Name</span>
          </div>
        )}
        {!settings.qr_vendor_branding && !settings.qr_show_farm_identity && (
          <div className="text-xs text-ink-muted italic">Only fruit information — no branding shown.</div>
        )}
      </div>
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function VendorsPage() {
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [qrSettings, setQRSettings] = useState<QRDisplaySettings>({
    qr_vendor_branding: true,
    qr_show_farm_identity: true,
    qr_farm_attribution_label: 'Grown by',
  })
  const [modalVendor, setModalVendor] = useState<Vendor | null | 'new'>(null)
  const [loading, setLoading] = useState(true)
  const [savedToast, setSavedToast] = useState(false)

  useEffect(() => {
    Promise.all([
      api.get<{ vendors: Vendor[] }>('/vendors').then(r => r.data),
      api.get<QRDisplaySettings>('/settings/qr-display').then(r => r.data).catch(() => null),
    ])
      .then(([vData, sData]) => {
        setVendors(vData.vendors ?? [])
        if (sData) setQRSettings(sData)
      })
      .finally(() => setLoading(false))
  }, [])

  const saveQRSettings = async (settings: QRDisplaySettings) => {
    setQRSettings(settings)
    try {
      await api.put('/settings/qr-display', settings)
      setSavedToast(true)
      setTimeout(() => setSavedToast(false), 2000)
    // eslint-disable-next-line no-empty
    } catch {}
  }

  const handleSaved = (saved: Vendor) => {
    setVendors(prev => {
      const idx = prev.findIndex(v => v.id === saved.id)
      if (idx >= 0) {
        const next = [...prev]
        next[idx] = saved
        return next
      }
      return [...prev, saved].sort((a, b) => a.name.localeCompare(b.name))
    })
    setModalVendor(null)
  }

  const handleDelete = async (vendor: Vendor) => {
    if (!confirm(`Remove ${vendor.name}? Historical sales records are kept.`)) return
    await api.delete(`/vendors/${vendor.id}`)
    setVendors(prev => prev.filter(v => v.id !== vendor.id))
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        <div className="skeleton h-8 w-48" />
        <div className="skeleton h-5 w-72" />
        <div className="space-y-3 mt-8">
          {[1, 2, 3].map(i => <div key={i} className="skeleton h-20 w-full rounded-xl" />)}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="section-title">Vendors</h1>
          <p className="section-subtitle">
            Retailers and buyers you sell produce to. Their branding can appear on QR scan pages.
          </p>
        </div>
        <button onClick={() => setModalVendor('new')} className="btn-primary self-start">
          <Plus size={16} strokeWidth={2.5} />
          Add vendor
        </button>
      </div>

      {vendors.length === 0 ? (
        <div className="empty-state mb-8">
          <div className="w-14 h-14 rounded-2xl bg-white border border-ink-ghost/20 mx-auto mb-4 flex items-center justify-center">
            <Store className="text-ink-muted" size={28} strokeWidth={1.5} />
          </div>
          <div className="font-semibold text-ink">No vendors yet</div>
          <p className="text-sm text-ink-soft mt-1 mb-5 max-w-sm mx-auto">
            Add retailers and buyers you sell to. Their logo will appear on the QR scan
            pages for the produce they purchase from you.
          </p>
          <button onClick={() => setModalVendor('new')} className="btn-primary">
            <Plus size={16} strokeWidth={2.5} />
            Add your first vendor
          </button>
        </div>
      ) : (
        <div className="space-y-2.5 mb-10">
          {vendors.map(v => (
            <VendorCard key={v.id} vendor={v} onEdit={() => setModalVendor(v)} onDelete={() => handleDelete(v)} />
          ))}
        </div>
      )}

      <h2 className="font-display text-lg font-bold text-ink mb-3">QR Scan Page Settings</h2>
      <QRSettingsPanel settings={qrSettings} onChange={saveQRSettings} />

      <div className="card p-5 mt-6 text-sm leading-relaxed text-ink-soft">
        <strong className="text-ink">How vendor QR branding works: </strong>
        When you record a harvest sale and tag it to a vendor, the plants in that harvest
        are linked. Next time a consumer scans a QR on one of those trees (or its fruit
        label), they see the vendor's logo and name alongside the tree's story. You
        control whether your farm name appears too.
      </div>

      {modalVendor !== null && (
        <VendorModal
          vendor={modalVendor === 'new' ? null : modalVendor}
          onClose={() => setModalVendor(null)}
          onSaved={handleSaved}
        />
      )}

      {savedToast && (
        <div role="status" aria-live="polite"
          className="fixed bottom-6 right-6 bg-ink text-white px-4 py-2.5 rounded-lg shadow-strong flex items-center gap-2 text-sm font-medium animate-slide-up z-40">
          <Check size={15} className="text-grove-400" />
          Settings saved
        </div>
      )}
    </div>
  )
}
